import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import * as moment from 'moment';

import { Router, ActivatedRoute, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { SessionService } from './session.service';

import { environment } from '../../environments/environment';
import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import { IpAddress, QuickPayInfo, QuickPayRequest, QuickPayUserInformation } from '../models/quickpay';
import { VerifyService } from './verify.service';

@Injectable({
  providedIn: 'root'
})
export class QuickPayService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _userSettings = environment.user;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  private quickPayInfoSessionName = 'quickPayInfo';
  private _quickpayInfo: QuickPayInfo;

  quickPayInfoSubject: BehaviorSubject<QuickPayInfo>;
  quickPayInfo: Observable<QuickPayInfo>;

  constructor(
    @Inject('BASE_URL') baseUrl: string,
    private _route: ActivatedRoute,
    private _router: Router,
    private _httpClient: HttpClient,
    private _sessionService: SessionService,
    httpErrorHandler: HttpErrorHandler
  ) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('QuickPayService');

    const sessionQuickPayInfo:QuickPayInfo = this._sessionService.getObjectFromSession(this.quickPayInfoSessionName);
    if (sessionQuickPayInfo !== null) {
      sessionQuickPayInfo.sessionCreateDate = new Date(sessionQuickPayInfo.sessionCreateDate);
      sessionQuickPayInfo.sessionLastActiveDate = new Date(sessionQuickPayInfo.sessionLastActiveDate);

      if (this._logging.userTraceLog) {
        console.log('QuickPayService.constructor :: getObjectFromSession = true');
      }
        this._quickpayInfo = sessionQuickPayInfo;
    } else {
      if (this._logging.userTraceLog) {
        console.log('QuickPayService.constructor :: getObjectFromSession = false');
      }
      this._quickpayInfo = this._getQuickPayUserInfo();
    }

    if (this._logging.userTraceLog) {
      console.log('QuickPayService.constructor :: _quickpayInfo = ' + JSON.stringify(this._quickpayInfo));
    }

    this.quickPayInfoSubject = new BehaviorSubject<QuickPayInfo>(this._quickpayInfo);
    this.quickPayInfo = this.quickPayInfoSubject.asObservable();

    this.quickPayInfo.subscribe(response => this._onUserInfoChanged(response), error => this._onError(error));
  }
  public CheckPartiId(logonRequest: QuickPayRequest): Observable<QuickPayUserInformation> {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/CheckPartiId';

    if (this._logging.serviceCallTraceLog) {
        console.log('QuickPayService.verifyUserName :: serviceUrl = ' + serviceUrl);
    }

    // return value is the db.userId so any value greater than 0 = verified UserName
    return this._httpClient.post<number>(serviceUrl, logonRequest).pipe(
        retry(this._apiSettings.retryCount),
        catchError(this._handleError('verifyUsername', null))
    );
}

  public verifyPartiLnameZip(quickPayRequest: QuickPayRequest): Observable<QuickPayUserInformation> {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/CheckPartiLnameZip';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPayService.verifyPartiLnameZip :: serviceUrl = ' + serviceUrl);
    }

    // return value is the db.userId so any value greater than 0 = verified UserName and Password
    return this._httpClient.post<number>(serviceUrl, quickPayRequest).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('verifyPartiLnameZip', null))
    );
  }

  public getNewQuickPayRequest(): QuickPayRequest {
    const newQuickPayRequest: QuickPayRequest = {
      acct_type: null,
      partiId: null,
      lastName: null,
      zipCode: null,
      retryCount: null,
      lockedOutFlag: null,
      activeFlag: null,
      multifactorEligible: null,
      multifactorEnabled: null,
      multifactorGracePeriod: null,
      lastVerifiedOTP: null,
      multifactorRetryCount: null,
      lastVerifiedIpAddress: null,
    };
    return newQuickPayRequest;
  }

  public getNewQuickPayUserInformation(): QuickPayUserInformation {
    const newQuickPayUserInformation: QuickPayUserInformation = {
      acct_type: null,
      partiId: null,
      firstName: null,
      middleInitial: null,
      lastName: null,
      zipCode: null,
      retryCount: null,
      lockedOutFlag: null,
      activeFlag: null,
      multifactorEligible: null,
      multifactorEnabled: null,
      multifactorGracePeriod: null,
      lastVerifiedOTP: null,
      multifactorRetryCount: null,
      lastVerifiedIpAddress: null,
      stdNum_count: null,
      note_count: null,
    };
    return newQuickPayUserInformation;
  }

  login(
    partiId: number,
    firstName: string,
    middleInitial: string,
    lastName: string,
    zipCode: string
  ): void {
    if (partiId !== null && partiId > 0 && partiId !== null && partiId > 0) {
      this._quickpayInfo = this._getQuickPayUserInfo();

      this._quickpayInfo.partiId = partiId.toString();
      this._quickpayInfo.firstName = firstName;
      this._quickpayInfo.middleInitial = middleInitial;
      this._quickpayInfo.lastName = lastName;
      this._quickpayInfo.zipCode = zipCode;
      this._quickpayInfo.authenticated = true;
      this._quickpayInfo.securityQuestionSetupFlag = true;

      if (this._logging.userTraceLog) {
        console.log('QuickPayService.quickpaylogin :: _quickpayInfo = ' + JSON.stringify(this._quickpayInfo));
      }

      this.quickPayInfoSubject.next(this._quickpayInfo);
    }
  }

  logout(sessionTimeout: boolean = false): void {
    this._quickpayInfo = this._getQuickPayUserInfo();
    this._quickpayInfo.sessionTimeout = sessionTimeout !== null ? sessionTimeout : false;

    if (this._logging.userTraceLog) {
      console.log('QuickPayService.logout :: _quickpayInfo = ' + JSON.stringify(this._quickpayInfo));
    }

    this.quickPayInfoSubject.next(this._quickpayInfo);
  }

  isLoggedIn(redirectOnNotLoggedIn: boolean = true): boolean {
    let isLoggedIn = this._quickpayInfo.authenticated;
    const timeoutDate = moment(new Date())
      .subtract(this._userSettings.timeoutInterval, 'm')
      .toDate();
    if (
      this._quickpayInfo.authenticated
    ) {
      this.quickPayInfoSubject.next(this._quickpayInfo);

      if (this._logging.userTraceLog) {
        console.log('QuickPayService.isLoggedIn :: sessionLastActiveDate is valid ');
      }
    } else {
      if (this._logging.userTraceLog) {
        console.log('QuickPayService.isLoggedIn :: sessionLastActiveDate is not valid ');
      }

      if (this._quickpayInfo.authenticated) {
        if (this._logging.userTraceLog) {
          console.log('QuickPayService.isLoggedIn :: force logout ');
        }

        this.logout(true);
      }
      isLoggedIn = false;
    }

    if (this._logging.userTraceLog) {
      console.log('QuickPayService.isLoggedIn :: isLoggedIn = ' + isLoggedIn);
      console.log('QuickPayService.isLoggedIn :: redirectOnNotLoggedIn = ' + redirectOnNotLoggedIn);
      console.log('QuickPayService.isLoggedIn :: _quickpayInfo = ' + JSON.stringify(this._quickpayInfo));
    }

    if (!isLoggedIn && redirectOnNotLoggedIn) {
      if (this._logging.userTraceLog) {
        console.log('QuickPayService.isLoggedIn :: force redirect to Login ');
      }

      if (this._logging.navigationTraceLog) {
        console.log('QuickPayService.isLoggedIn');
      }
      this._router.navigate(['login']);
    }

    return isLoggedIn;
  }

  hasSecurityQuestionSetupFlag(): boolean {
    return this._quickpayInfo.securityQuestionSetupFlag !== null && this._quickpayInfo.securityQuestionSetupFlag;
  }

  setSecurityQuestionSetupFlag(securityQuestionSetupFlag: boolean): void {
    if (this._quickpayInfo.authenticated) {
        this._quickpayInfo.securityQuestionSetupFlag = securityQuestionSetupFlag;
        this.quickPayInfoSubject.next(this._quickpayInfo);
    }
  }

  getFirstName(): string {
    return this._quickpayInfo.firstName !== null && this._quickpayInfo.firstName.length > 0 ? this._quickpayInfo.firstName : null;
  }

  getLastName(): string {
    return this._quickpayInfo.lastName !== null && this._quickpayInfo.lastName.length > 0 ? this._quickpayInfo.lastName : null;
  }

  getZipCode(): string {
    return this._quickpayInfo.zipCode !== null && this._quickpayInfo.zipCode.length > 0 ? this._quickpayInfo.zipCode : null;
  }

  getFullName(): string {
    const fullName: string =
      (this.getFirstName() !== null ? this.getFirstName() : '') + ' ' + (this.getLastName() !== null ? this.getLastName() : '');
    return fullName.trim();
  }

  hasParticipantId(): boolean {
    return this._quickpayInfo.partiId !== null && this._quickpayInfo.partiId.length > 0;
  }

  getAcctType(): string {
    return this._quickpayInfo.acct_type !== null && this._quickpayInfo.acct_type.length > 0 ? this._quickpayInfo.acct_type : null;
  }

  getParticipantId(): string {
    return this._quickpayInfo.partiId !== null && this._quickpayInfo.partiId.length > 0 ? this._quickpayInfo.partiId : null;
  }

  getNoteId(): number {
    return this._quickpayInfo.noteId !== null && this._quickpayInfo.noteId > 0 ? this._quickpayInfo.noteId : null;
  }

  setNoteId(noteId: number): void {
    if (this._quickpayInfo.authenticated) {
      this._quickpayInfo.noteId = noteId;
      this.quickPayInfoSubject.next(this._quickpayInfo);
    }
  }

  clearNoteId(): void {
    if (this._quickpayInfo.authenticated) {
      this._quickpayInfo.noteId = null;
      this.quickPayInfoSubject.next(this._quickpayInfo);
    }
  }

  hasRelationshipType(): boolean {
    return this._quickpayInfo.relationshipType !== null && this._quickpayInfo.relationshipType.length > 0;
  }

  getRelationshipType(): string {
    return this._quickpayInfo.relationshipType !== null && this._quickpayInfo.relationshipType.length > 0 ? this._quickpayInfo.relationshipType : null;
  }

  setRelationshipType(relationshipType: string): void {
    if (this._quickpayInfo.authenticated) {
        this._quickpayInfo.relationshipType = relationshipType;
        this.quickPayInfoSubject.next(this._quickpayInfo);
    }
  }

  clearRelationshipType(): void {
    if (this._quickpayInfo.authenticated) {
        this._quickpayInfo.relationshipType = null;
        this.quickPayInfoSubject.next(this._quickpayInfo);
    }
  }

  public getPublicIpAddress(): Observable<IpAddress> {
    const serviceUrl: string = this._apiSettings.publicIpAddressServiceUrl;
    if (this._logging.serviceCallTraceLog) {
        console.log('UserService.getPublicIpAddress :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.get<IpAddress>(serviceUrl).pipe(
        retry(this._apiSettings.retryCount),
        catchError(this._handleError('getPublicIpAddress', { ip: '' }))
    );
  }

  private _getQuickPayUserInfo(): QuickPayInfo {
    const newQuickPayUserInfo: QuickPayInfo = {
      acct_type: null,
      email: null,
      firstName: null,
      middleInitial: null,
      lastName: null,
      zipCode: null,
      authenticated: false,
      securityQuestionSetupFlag: false,
      partiId: null,
      noteId: null,
      relationshipType: null,
      sessionToken: null,
      sessionCreateDate: null,
      sessionLastActiveDate: null,
      sessionTimeout: false
    };
    return newQuickPayUserInfo;
  }

  private _onUserInfoChanged(newUserInfo: QuickPayInfo): void {
    this._sessionService.setObjectToSession(this.quickPayInfoSessionName, newUserInfo);

    if (this._logging.userTraceLog) {
      console.log('QuickPayService.onUserInfoChanged :: newQuickpayInfo = ' + JSON.stringify(newUserInfo));
    }
  }

  private _onError(error: any) {
    if (this._logging.errorTraceLogging) {
      console.log('QuickPayService._onError :: ' + JSON.stringify(error));
    }
    console.error(error);
  }
}

// **********************************************
// Route Guards
// **********************************************

 @Injectable()
export class QP_OnlyLoggedInUsersGuard implements CanActivate {
  private _logging = environment.logging;

   constructor(private _router: Router, private _userService: QuickPayService, private _verifyService: VerifyService) { }

   canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

    let isLoggedIn: boolean = this._userService.isLoggedIn(false);
    let requiresVerification: boolean = this._verifyService.requiresVerification();

    if (isLoggedIn) {
        if (!requiresVerification) {
            if (this._logging.userTraceLog) {
                console.log('QP_OnlyLoggedInUsersGuard.canActivate() = true - verified');
            }
            return true;
        } else {
            if (this._logging.userTraceLog) {
                console.log('QP_OnlyLoggedInUsersGuard.canActivate() = false - not verified');
            }
            this._router.navigate(['verify'])
            return false;
        }
    }
    else {
        if (this._logging.userTraceLog) {
            console.log('QP_OnlyLoggedInUsersGuard.canActivate() = false');
        }
        this._router.navigate(['quickpay']);
        return false;
    }
  }
}

 @Injectable()
export class QP_OnlyHasParticipantIdUsersGuard implements CanActivate {
  private _logging = environment.logging;

   constructor(private _router: Router, private _userService: QuickPayService) { }

   canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
       if (this._userService.hasSecurityQuestionSetupFlag()) {
           if (this._logging.userTraceLog) {
               console.log('QP_OnlyHasParticipantIdUsersGuard.canActivate() = true');
           }
           return true;
       } else {
           if (this._logging.userTraceLog) {
               console.log('QP_OnlyHasParticipantIdUsersGuard.canActivate() = false');
           }
           this._router.navigate(['quickpay']);
           return false;
       }
   }
}
